import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, FormControlLabel, IconButton, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Field, FieldLabel } from '../../UI';
import { LinkAdicionar } from '../abas/BlocosCadastrais';
import * as C from '../../../theme/colors.constants';

/**
 * Telefones da aba Devedor (AW2-1134 Etapa 4) — porta de `TelefonesV2/Telefones.jsx`
 * real, mesmo padrão estrutural de `EnderecoDevedor.jsx` (Etapa 3): lista
 * real de múltiplos itens, add/remove/"Principal" mutuamente exclusivo.
 *
 * Diferenças confirmadas por leitura direta do código-fonte real em relação
 * a Endereços:
 * - Ícone de lixeira aparece em TODOS os itens, inclusive o índice 0 —
 *   `TelefoneDevedor.jsx` real não passa `showTrashAtFirstItem` para
 *   `Telefones.jsx` (default `false`), e o cálculo real
 *   (`showTrashAtFirstItemHandler`) só omite a lixeira do primeiro item
 *   quando esse prop é `true`. Comportamento genuinamente diferente de
 *   Endereços, não um descuido.
 * - "WhatsApp" NÃO é mutuamente exclusivo entre os telefones — `TelefoneDevedor.jsx`
 *   real não passa `validaWhatsApp` (default `false` em `Telefones.jsx`),
 *   então cada telefone liga/desliga WhatsApp de forma independente.
 * - Ícone de copiar (`ContentCopyIcon`, `navigator.clipboard.writeText(ddd+numero)`)
 *   condicionado só a `podeModificarFase` (`exibirBotaoCopiar={podeModificarFase}`
 *   real) — independente de `isEditMode`, mesmo fora do modo de edição o
 *   ícone aparece contanto que a fase permita.
 *
 * Deliberadamente NÃO portado nesta etapa: máscara dinâmica de telefone por
 * tipo (`maskTelefone()` real — Celular/Residencial/Comercial com heurística
 * por 1º dígito) — mesma decisão já tomada para CPF/CNPJ (Etapa 2) e CEP
 * (Etapa 3): campo de texto livre nesta etapa, sem validação de formato ao
 * vivo.
 */
export default function TelefoneDevedor({
  telefones, tiposTelefoneOptions, podeModificarFase, isEditMode,
  onChangeTelefone, onAdicionarTelefone, onRemoverTelefone, onMarcarPrincipal,
}) {
  const desabilitado = !podeModificarFase || !isEditMode;
  const exibirBotaoCopiar = Boolean(podeModificarFase);

  // Achado ao vivo: `navigator.clipboard.writeText` pode rejeitar (permissão
  // negada, documento sem foco) e produz uma exceção não tratada no console
  // sem tratamento algum. Produção (`Telefone.jsx` real, `onCopy`) não trata
  // esse rejeito — divergência deliberada aqui, mesmo custo zero de
  // contrato (a ação em si é a mesma). `console.warn` no catch, não um
  // catch mudo — mesma regra já documentada no módulo para nunca engolir em
  // silêncio total uma falha que o usuário poderia precisar saber (aqui o
  // usuário só percebe a falha se checar o console, mas ao menos fica um
  // rastro para diagnóstico, em vez de uma falha 100% silenciosa).
  const onCopiar = (telefone) => {
    navigator.clipboard.writeText(`${telefone.ddd ?? ''}${telefone.numero ?? ''}`)
      .catch(erro => console.warn('Falha ao copiar telefone para a área de transferência', erro));
  };

  return (
    <>
      <LinkAdicionar onClick={onAdicionarTelefone} disabled={desabilitado}>
        Adicionar Telefone +
      </LinkAdicionar>

      {telefones.map((telefone, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr) minmax(0, 1fr)',
                md: exibirBotaoCopiar
                  ? 'minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 1fr) minmax(0, 0.4fr) minmax(0, 1.2fr)'
                  : 'minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 1fr) minmax(0, 1.2fr)',
              },
              gap: 'clamp(10px, 0.8vw, 22px)',
              alignItems: 'end',
            }}
          >
            <Field
              label="Tipo de telefone:"
              tipo="select"
              name="tipoTelefone"
              options={tiposTelefoneOptions}
              value={telefone.tipoTelefone}
              onChange={e => onChangeTelefone(index, 'tipoTelefone', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="DDD:"
              name="ddd"
              value={telefone.ddd}
              onChange={e => onChangeTelefone(index, 'ddd', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Telefone:"
              name="numero"
              value={telefone.numero}
              onChange={e => onChangeTelefone(index, 'numero', e.target.value)}
              disabled={desabilitado}
            />
            {exibirBotaoCopiar && (
              <Box>
                <FieldLabel>&nbsp;</FieldLabel>
                <Tooltip title="Copiar telefone">
                  <IconButton
                    size="small"
                    aria-label="Copiar telefone"
                    onClick={() => onCopiar(telefone)}
                    sx={{ color: C.navy }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Field
              label="Observação:"
              name="observacao"
              value={telefone.observacao}
              onChange={e => onChangeTelefone(index, 'observacao', e.target.value)}
              disabled={desabilitado}
            />
          </Box>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mt: 1,
          }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(telefone.principal)}
                  onChange={() => onMarcarPrincipal(index)}
                  disabled={desabilitado}
                />
              )}
              label={<Typography variant="body2">Principal</Typography>}
              sx={{ ml: 0 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(telefone.permitirWhatsApp)}
                  onChange={e => onChangeTelefone(index, 'permitirWhatsApp', e.target.checked)}
                  disabled={desabilitado}
                />
              )}
              label={<Typography variant="body2">WhatsApp</Typography>}
              sx={{ ml: 0 }}
            />
            <Tooltip title="Excluir Telefone">
              <span>
                <IconButton
                  size="small"
                  aria-label="Excluir Telefone"
                  onClick={() => onRemoverTelefone(index)}
                  disabled={desabilitado}
                  sx={{ color: C.navy }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </>
  );
}

TelefoneDevedor.propTypes = {
  telefones: PropTypes.arrayOf(PropTypes.shape({
    idTelefone: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idEntidadeTelefone: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tipoTelefone: PropTypes.string,
    ddd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    numero: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    observacao: PropTypes.string,
    principal: PropTypes.bool,
    permitirWhatsApp: PropTypes.bool,
  })),
  tiposTelefoneOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
  })),
  podeModificarFase: PropTypes.bool,
  isEditMode: PropTypes.bool,
  onChangeTelefone: PropTypes.func,
  onAdicionarTelefone: PropTypes.func,
  onRemoverTelefone: PropTypes.func,
  onMarcarPrincipal: PropTypes.func,
};

TelefoneDevedor.defaultProps = {
  telefones: [],
  tiposTelefoneOptions: [],
  podeModificarFase: false,
  isEditMode: false,
  onChangeTelefone: () => null,
  onAdicionarTelefone: () => null,
  onRemoverTelefone: () => null,
  onMarcarPrincipal: () => null,
};
